import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DocumentTypeSelect } from '@/seller/components/documents/DocumentTypeSelect';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidatingInput, ValidationScope } from '@/components/forms/validation';
import { useValidationManager } from '@/utils/hooks';
import { DocumentType } from '@/types';
import AttachmentList from '@/components/AttachmentList';
import { ExternalAgreementFileCreateOptions } from '@/services/external-agreements';

export interface ExternalAgreementMainInfo {
    name: string;
    documentType: DocumentType | null;
    files: ExternalAgreementFileCreateOptions[];
}

interface ExternalAgreementCreateWizardStepMainProps {
    value: ExternalAgreementMainInfo;
    onChange?(data: ExternalAgreementMainInfo): void;
    profileId: number;
}

const ExternalAgreementCreateWizardStepMain: React.FC<ExternalAgreementCreateWizardStepMainProps> = ({
    value,
    onChange,
    profileId,
}) => {
    const triggerChange = (changes: Partial<ExternalAgreementMainInfo>) => {
        triggerHandler(onChange, { ...value, ...changes });
    };

    const handleDocumentTypeChange = (id: number, type: DocumentType | null) => {
        triggerChange({ documentType: type });
    };

    const validationManager = useValidationManager([
        {
            isValid: (params) => value.files && value.files.length > 0,
            summaryMessage: () => <Trans>Agreement document is required</Trans>,
        },
    ]);

    return (
        <ValidationScope validationManager={validationManager}>
            <div className="form columns-1">
                <label>
                    <Trans>Agreement type</Trans>
                    <div className="form-control-unstyled">
                        <DocumentTypeSelect
                            placeholder={t`Please select...`}
                            fieldName={t`Agreement type`}
                            filter={(type) => type.externalAgreementAllowed && type.permissions.canUploadDocuments}
                            value={value.documentType ? value.documentType.id : ''}
                            onChange={handleDocumentTypeChange}
                            required
                        />
                    </div>
                </label>
                <label>
                    <Trans>Agreement name</Trans>

                    <ValidatingInput
                        type="text"
                        fieldName={t`Agreement name`}
                        placeholder={t`The text that the buyer will see`}
                        className="form-control"
                        value={value.name}
                        onChange={(e) => triggerChange({ name: e.target.value })}
                        required
                    />
                </label>
                <div className="form-label">
                    <Trans>Agreement files</Trans>

                    <AttachmentList
                        value={value.files}
                        showVisibilityFlags={true}
                        canAdd
                        canEdit
                        onChange={(newFiles) => triggerChange({ files: newFiles })}
                        ownerProfileId={profileId}
                        allowPaste
                    />
                </div>
            </div>
        </ValidationScope>
    );
};

export default ExternalAgreementCreateWizardStepMain;
